import React from "react";
import { motion } from "framer-motion";
import {
  ShieldCheckIcon,
  ClipboardDocumentCheckIcon,
  CpuChipIcon,
  GlobeAltIcon,
  LockClosedIcon,
  CheckBadgeIcon,
  BuildingOfficeIcon,
  DocumentTextIcon,
  KeyIcon
} from "@heroicons/react/24/outline";

const ComplianceFrameworksPage = () => {
  const frameworks = [
    {
      icon: ShieldCheckIcon,
      name: "ISO/IEC 27001:2022",
      description: "International standard for information security management systems (ISMS).",
      gradient: "from-brand to-brand-dark"
    },
    {
      icon: ClipboardDocumentCheckIcon,
      name: "SOC 2",
      description: "Ensures secure handling of data based on trust principles, confidentiality, and privacy.",
      gradient: "from-green-500 to-emerald-500"
    },
    {
      icon: LockClosedIcon,
      name: "HIPAA",
      description: "Ensures the protection of health information by setting standards for secure handling, storage, and transmission of patient data.",
      gradient: "from-purple-500 to-violet-500"
    },
    {
      icon: KeyIcon,
      name: "PCI DSS",
      description: "Establishes standards for securing payment card data, preventing fraud, and ensuring compliance.",
      gradient: "from-red-500 to-pink-500"
    },
    {
      icon: CpuChipIcon,
      name: "NIST Cybersecurity",
      description: "NIST CSF helps manage and protect against cybersecurity risks through a structured framework.",
      gradient: "from-orange-500 to-yellow-500"
    },
    {
      icon: CheckBadgeIcon,
      name: "CYBER ESSENTIAL",
      description: "Certifies organizations for implementing baseline security controls against cyber threats.",
      gradient: "from-teal-500 to-cyan-500"
    },
    {
      icon: BuildingOfficeIcon,
      name: "FedRAMP",
      description: "Ensures cloud providers meet U.S. security standards for safeguarding federal data.",
      gradient: "from-indigo-500 to-purple-500"
    },
    {
      icon: GlobeAltIcon,
      name: "HITRUST",
      description: "Ensures protection of sensitive data through unified security and compliance standards.",
      gradient: "from-pink-500 to-red-500"
    },
    {
      icon: DocumentTextIcon,
      name: "ISO/27701:2019",
      description: "A framework for managing privacy information and enhancing privacy controls within an organization.",
      gradient: "from-violet-500 to-purple-500"
    }
  ];

  const benefits = [
    "AI frameworks simplify complex compliance processes",
    "Automation boosts accuracy and reduces compliance risks",
    "Proactive adherence ensures regulatory excellence consistently"
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative px-6 pt-12 pb-20 overflow-hidden">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <div className="inline-flex items-center px-4 py-2 bg-white/10 rounded-full border border-white/20 mb-6">
              <ShieldCheckIcon className="w-5 h-5 text-cyan-400 mr-2" />
              <span className="text-sm font-medium text-cyan-200">Navigate Compliance with Confidence</span>
            </div>
            
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
              Compliance
              <span className="bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent block">
                Frameworks
              </span>
            </h1>
            
            <p className="text-xl md:text-2xl text-gray-300 max-w-4xl mx-auto mb-12 leading-relaxed">
              Navigate compliance seamlessly with AI-powered frameworks. Simplify processes, 
              enhance accuracy, and drive effortless adherence. Achieve regulatory excellence 
              with innovative automation and proactive risk management.
            </p>

            <div className="flex items-center justify-center mb-12">
              <img 
                src="https://images.unsplash.com/photo-1464759326739-1dc3058a40bf?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHwxfHxjb21wbGlhbmNlfGVufDB8fHxibHVlfDE3NTQ5MjU3NzZ8MA&ixlib=rb-4.1.0&q=85" 
                alt="Modern Compliance Architecture and Frameworks"
                className="max-w-2xl w-full h-auto rounded-2xl shadow-2xl border border-white/20"
              />
            </div>

            {/* Benefits */}
            <div className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-sm rounded-3xl p-8 border border-white/20">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {benefits.map((benefit, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: index * 0.2 }}
                    className="flex items-center space-x-3"
                  >
                    <div className="w-2 h-2 bg-cyan-400 rounded-full flex-shrink-0"></div>
                    <p className="text-gray-300 text-sm">{benefit}</p>
                  </motion.div>
                ))}
              </div>
            </div>
          </motion.div>
        </div>

        {/* Background Elements */}
        <div className="absolute inset-0 -z-10">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-cyan-500/20 rounded-full blur-3xl"></div>
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-blue-500/20 rounded-full blur-3xl"></div>
        </div>
      </section>

      {/* Frameworks Grid */}
      <section className="relative px-6 py-20 bg-gradient-to-r from-slate-900/50 to-blue-900/30 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">Framework & Standards</h2>
            <p className="text-xl text-gray-300 max-w-4xl mx-auto">
              HabileSec's AI-driven Tanikkai supports tailored compliance frameworks, ensuring flexibility, 
              security, and regulatory alignment across regions and industries.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {frameworks.map((framework, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                whileHover={{ scale: 1.02, y: -5 }}
                className="group bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-sm rounded-2xl p-8 border border-white/20 hover:border-white/40 transition-all duration-300"
              >
                <div className="flex items-center mb-6">
                  <div className={`w-14 h-14 bg-gradient-to-r ${framework.gradient} rounded-xl flex items-center justify-center mr-4 group-hover:scale-110 transition-transform`}>
                    <framework.icon className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-white group-hover:text-cyan-300 transition-colors">
                    {framework.name}
                  </h3>
                </div>
                
                <p className="text-gray-300 leading-relaxed text-sm">
                  {framework.description}
                </p>

                {/* AI-Powered Badge */}
                <div className="mt-6 flex items-center justify-between">
                  <span className="inline-flex items-center px-3 py-1 bg-gradient-to-r from-cyan-500/20 to-blue-500/20 rounded-full border border-cyan-400/30">
                    <CpuChipIcon className="w-4 h-4 text-cyan-400 mr-1" />
                    <span className="text-xs font-medium text-cyan-300">AI-Enhanced</span>
                  </span>
                  <span className="text-xs text-green-400 font-medium">Automated Compliance</span>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative px-6 py-20">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-sm rounded-3xl p-12 border border-white/20"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Ready to Automate Your Compliance?
            </h2>
            <p className="text-xl text-gray-300 mb-8">
              Streamline your compliance journey with AI-powered automation across all major frameworks
            </p>
            
            <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-6">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="bg-gradient-to-r from-cyan-500 to-blue-600 text-white px-8 py-4 rounded-2xl font-bold text-lg hover:from-cyan-600 hover:to-blue-700 transition-all shadow-2xl border-2 border-white/20"
              >
                Start Free Trial
              </motion.button>
              
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="bg-white/10 backdrop-blur-sm text-white px-8 py-4 rounded-2xl font-bold text-lg hover:bg-white/20 transition-all border-2 border-white/30"
              >
                Schedule Demo
              </motion.button>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default ComplianceFrameworksPage;